<script setup lang="ts">
import useLocale from '../../composables/use-locale';

type Props = {
  showHeader?: boolean;
  showBackButton?: boolean;
  backButtonPos?: 'bottomLeft' | 'topLeft';
};

withDefaults(defineProps<Props>(), {
  showHeader: true,
  showBackButton: false,
  backButtonPos: 'bottomLeft',
});

const { t } = useLocale();

const emit = defineEmits<{
  back: [];
}>();

const handleBack = () => {
  emit('back');
};
</script>

<template>
  <v-container fluid class="quizter-layout pa-6">
    <div
      class="header-wrapper text-white d-flex justify-space-between align-center mb-4 my-sm-0"
      :class="{ 'show-header': showHeader || (showBackButton && backButtonPos === 'topLeft') }"
    >
      <div v-if="showHeader">
        <div class="chalkduster-font text-h4 text-sm-h5">Quizter!</div>
        <div class="header-right-slot">
          <slot name="header-right" />
        </div>
      </div>
      <div v-else>
        <!-- Back button top left (only if header is not visible) -->
        <v-btn variant="text" color="white" class="back-button text-none chalkduster-font mt-2" @click="handleBack">
          <template #prepend>
            <v-icon icon="fa:fawsb fa-arrow-left" class="mr-2"></v-icon>
          </template>
          <span class="chalkduster-font">{{ t('back') }}</span>
        </v-btn>
      </div>
    </div>

    <!-- Back button bottom left (default) -->
    <div class="back-button-bottom-wrapper d-flex flex-column justify-start align-center pt-sm-4">
      <div>
        <slot></slot>
        <v-btn
          v-if="showBackButton && backButtonPos === 'bottomLeft'"
          variant="text"
          color="white"
          class="back-button text-none chalkduster-font mt-2"
          @click="handleBack"
        >
          <template #prepend>
            <v-icon icon="fa:fawsb fa-arrow-left" class="mr-2"></v-icon>
          </template>
          <span class="chalkduster-font">{{ t('back') }}</span>
        </v-btn>
      </div>
    </div>
  </v-container>
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'vuetify/settings' as settings;

@font-face {
  font-family: 'Chalkduster';
  src: url('../assets/Chalkduster.ttf') format('truetype');
  font-weight: normal;
  font-style: normal;
}

@font-face {
  font-family: 'Chalkboard';
  src: url('../assets/Chalkboard Regular.ttf') format('truetype');
  font-weight: normal;
  font-style: normal;
}

.quizter-layout {
  background-image: url('../assets/chalkboard.webp');
  background-size: cover;
  background-attachment: fixed;
  min-height: 100%;
}

.header-wrapper {
  position: relative;
  visibility: hidden;
}

.show-header {
  visibility: visible;
}

.back-button-bottom-wrapper {
  min-height: 60vh;
}

:deep(.chalkduster-font) {
  font-family: 'Chalkduster', sans-serif;
  font-weight: normal;
}

:deep(.chalkboard-font) {
  font-family: 'Chalkboard', sans-serif;
  font-weight: normal;
}

:deep(span) {
  font-family: 'Chalkboard', sans-serif;
  font-weight: normal;
}

:deep(.text-h2) {
  font-family: 'Chalkboard', sans-serif;
  font-size: 1.5rem !important;

  @media #{map.get(settings.$display-breakpoints, 'sm-and-up')} {
    font-size: 2.5rem !important;
  }
}

:deep(.text-h1) {
  font-size: 3.5rem !important;
}

// Global fade transition
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.header-right-slot {
  position: absolute;
  right: 0;
  top: 0;

  @media #{map.get(settings.$display-breakpoints, 'sm-and-up')} {
    top: -14px;
  }
}
</style>
